// src/components/HRComponents/PolicyViewDetails.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Send, CheckCircle, Archive, Users, ClipboardList, Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PolicyVersionTimeline from './PolicyVersionTimeline';
import PolicyDocumentViewer from './PolicyDocumentViewer';
import PolicyStatusBadge from './PolicyStatusBadge';
import PolicyUploadDropzone from './PolicyUploadDropzone';
import DeleteConfirmationOffcanvas from '../../helpers/DeleteConfirmationOffcanvas';
import ContentProtector from '../../helpers/ContentProtector';

const PolicyViewDetails = () => {
  const { portalName, id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [ackStats, setAckStats] = useState({ acknowledgedCount: 0, pendingCount: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // States for New Version Creation Modal
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [vSubmitting, setVSubmitting] = useState(false);
  const [vSourceType, setVSourceType] = useState('uploaded'); // 'uploaded' | 'authored'
  const [vChangeSummary, setVChangeSummary] = useState('');
  const [vFile, setVFile] = useState(null);
  const [vFileError, setVFileError] = useState(null);
  const [vContentHtml, setVContentHtml] = useState('');

  // State for confirming Archive via custom Delete Offcanvas Modal
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);

  // Fetch all policy document metrics, details, and versions
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, versionsRes, ackRes] = await Promise.all([
        api.get(SummaryApi.getPolicyById.url(id)),
        api.get(SummaryApi.viewPolicyVersionById.url(id)),
        api.get(SummaryApi.getPolicyAcknowledgementsReport.url(id), { params: { limit: 1 } }),
      ]);
      
      if (docRes.data.success) setDoc(docRes.data.document);
      if (versionsRes.data.success) setVersions(versionsRes.data.versions || []);
      if (ackRes.data.success) {
        setAckStats({
          acknowledgedCount: ackRes.data.acknowledgedCount,
          pendingCount: ackRes.data.pendingCount,
          total: ackRes.data.total,
        });
      }
    } catch (error) {
      toast.error('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Submit the current draft version for approval
  const handleSubmitForApproval = async () => {
    const draftVersion = versions.find(v => v.status === 'draft');
    if (!draftVersion) return toast.error('No draft version found to submit');

    setActionLoading(true);
    try {
      await api.post(SummaryApi.submitPolicyApproval.url(id), { versionId: draftVersion._id });
      toast.success('Document successfully submitted for approval');
      fetchAll();
    } catch (error) {
      toast.error('Failed to submit for approval');
    } finally {
      setActionLoading(false);
    }
  };

  // Approve the pending version
  const handleApprove = async () => {
    const pendingVersion = versions.find(v => v.status === 'pending_approval');
    if (!pendingVersion) return toast.error('No pending version found to approve');

    setActionLoading(true);
    try {
      await api.post(SummaryApi.approvalPolicy.url(id), { versionId: pendingVersion._id });
      toast.success('Document approved successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle publishing flow
  const handlePublish = async () => {
    setActionLoading(true);
    try {
      await api.post(SummaryApi.publishPolicy.url(id));
      toast.success('Document published — employees have been notified');
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to publish');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle archiving flow once confirmed in the offcanvas modal
  const handleArchive = async () => {
    setActionLoading(true);
    try {
      await api.post(SummaryApi.archivePolicy.url(id));
      toast.success('Document archived successfully');
      setIsArchiveOpen(false); // Close the offcanvas modal
      fetchAll();
    } catch (error) {
      toast.error('Failed to archive document');
    } finally {
      setActionLoading(false);
    }
  };

  // Send reminder notifications manually to pending employees
  const handleRemind = async () => {
    try {
      await api.post(SummaryApi.bulkRemindPolicy.url, { policyDocumentId: id });
      toast.success('Reminders sent');
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  // Handle saving the new drafted version of this policy
  const handleVersionSubmit = async (e) => {
    e.preventDefault();
    if (vSourceType === 'uploaded' && !vFile) return toast.error('Please attach a PDF or Word file');
    if (vSourceType === 'authored' && !vContentHtml.trim()) return toast.error('Please enter document content');

    setVSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('sourceType', vSourceType);
      payload.append('changeSummary', vChangeSummary);

      if (vSourceType === 'uploaded' && vFile) {
        payload.append('file', vFile);
      } else if (vSourceType === 'authored') {
        payload.append('contentHtml', vContentHtml);
      }

      const res = await api.post(SummaryApi.createPolicyVersions.url(id), payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        toast.success('New version created as draft successfully');
        setShowVersionModal(false);
        setVChangeSummary('');
        setVFile(null);
        setVContentHtml('');
        fetchAll();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create new version');
    } finally {
      setVSubmitting(false);
    }
  };

  if (loading || !doc) {
    return <div className="p-8 text-center text-gray-400">Loading...</div>;
  }

  const currentVersion = doc.currentVersionId;

  // Identify active versions in draft or pending approval states
  const hasDraftVersion = versions.some(v => v.status === 'draft');
  const hasPendingApprovalVersion = versions.some(v => v.status === 'pending_approval');
  
  // FIX: Identify if there are any publishable versions (either draft or approved) [1]
  const hasPublishableVersion = versions.some(v => v.status === 'draft' || v.status === 'approved');

  return (
    <div className="p-4">
      <ContentProtector />
      {/* Header section with back navigation and document status */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 p-1.5 sm:p-2">
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">{doc.title}</h1>
            <p className="text-xs text-gray-500">{doc.policyCode}</p>
          </div>
        </div>
        <PolicyStatusBadge status={doc.status} />
      </div>

      {/* Assignment Metrics Card Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Users className="h-4 w-4" /> Total Assigned</div>
          <p className="text-2xl font-bold text-gray-900">{ackStats.total}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-600 text-xs mb-1"><CheckCircle className="h-4 w-4" /> Acknowledged</div>
          <p className="text-2xl font-bold text-gray-900">{ackStats.acknowledgedCount}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-yellow-600 text-xs mb-1"><ClipboardList className="h-4 w-4" /> Pending</div>
          <p className="text-2xl font-bold text-gray-900">{ackStats.pendingCount}</p>
        </div>
      </div>

      {/* Primary Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Show Submit button dynamically if any draft version exists [1] */}
        {hasDraftVersion && doc.status !== 'pending_approval' && (
          <button onClick={handleSubmitForApproval} disabled={actionLoading} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            <Send className="h-4 w-4" /> Submit for Approval
          </button>
        )}

        {/* Show Approve button dynamically if any pending approval version exists [1] */}
        {hasPendingApprovalVersion && (
          <button onClick={handleApprove} disabled={actionLoading} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            <CheckCircle className="h-4 w-4" /> Approve Document
          </button>
        )}

        {/* FIX: Checked hasPublishableVersion to ensure Publish button renders cleanly on Approved states! [1] */}
        {hasPublishableVersion && (doc.status === 'draft' || doc.status === 'approved' || doc.status === 'pending_approval' || doc.status === 'published' || doc.status === 'archived') && (
          <button onClick={handlePublish} disabled={actionLoading} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            <CheckCircle className="h-4 w-4" /> Publish
          </button>
        )}

        {doc.status === 'published' && (
          <>
            <button onClick={handleRemind} className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm">
              <Send className="h-4 w-4" /> Send Reminders
            </button>
            <button onClick={() => setIsArchiveOpen(true)} className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm text-red-600">
              <Archive className="h-4 w-4" /> Archive
            </button>
          </>
        )}
        <button onClick={() => navigate(`/${portalName}/hr/letter-policy/assign/${id}`)} className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm">
           <Users className="h-4 w-4" /> Manage Assignment
        </button>
        <button onClick={() => navigate(`/${portalName}/hr/letter-policy/acknowledgements/${id}`)} className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm">
           <ClipboardList className="h-4 w-4" /> Acknowledgement Report
        </button>
      </div>

      {/* Current active published version preview */}
      {currentVersion && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Current Version Preview</h2>
          <PolicyDocumentViewer
            policyId={id}
            fileUrl={currentVersion.fileUrl}
            mimeType={currentVersion.fileMimeType}
            fileName={currentVersion.fileName}
          />
        </div>
      )}

      {/* Version History section with "Add New Version" button */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700">Version History</h2>
          {/* We allow adding new versions ALWAYS to support reactivations and newer drafts [1] */}
          {doc.status !== 'pending_approval' && (
            <button
              onClick={() => setShowVersionModal(true)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              <Plus className="h-4 w-4" /> Add New Version
            </button>
          )}
        </div>
        <PolicyVersionTimeline versions={versions} />
      </div>

      {/* Cohesive "Add New Version" Modal */}
      {showVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <form onSubmit={handleVersionSubmit} className="bg-white rounded-xl border max-w-lg w-full p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between pb-2 border-b">
              <h3 className="text-lg font-bold text-gray-900">Add New Version</h3>
              <button
                type="button"
                onClick={() => setShowVersionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">What has changed in this version? (Summary)</label>
              <input
                type="text"
                value={vChangeSummary}
                onChange={(e) => setVChangeSummary(e.target.value)}
                placeholder="e.g., Updated remote work clauses, corrected grammatical errors."
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Content Source</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="radio" checked={vSourceType === 'uploaded'} onChange={() => setVSourceType('uploaded')} />
                  Upload File (PDF / Word)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="radio" checked={vSourceType === 'authored'} onChange={() => setVSourceType('authored')} />
                  Write In-App
                </label>
              </div>
            </div>

            {vSourceType === 'uploaded' ? (
              <PolicyUploadDropzone
                file={vFile}
                onFileSelect={(f, err) => { setVFile(f); setVFileError(err); }}
                error={vFileError}
              />
            ) : (
              <textarea
                rows={6}
                placeholder="Type or paste the new document content here..."
                value={vContentHtml}
                onChange={(e) => setVContentHtml(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
              />
            )}

            <div className="flex justify-end gap-3 pt-2 border-t">
              <button
                type="button"
                onClick={() => setShowVersionModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={vSubmitting}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
              >
                {vSubmitting ? 'Uploading...' : 'Save Draft Version'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation Offcanvas for Archiving with custom warnings and text */}
      <DeleteConfirmationOffcanvas
        isOpen={isArchiveOpen}
        onClose={() => setIsArchiveOpen(false)}
        onConfirm={handleArchive}
        title="Archive Policy Document"
        itemName={doc.title}
        confirmButtonText="Archive"
        isLoading={actionLoading}
        customDescription={
          <span>
            Are you sure you want to archive <span className="font-semibold text-gray-800">{doc.title}</span>? 
            Once archived, this document will be <span className="text-red-600 font-semibold">hidden from all employee self-service portals</span> and can no longer be acknowledged by pending users.
          </span>
        }
      />
    </div>
  );
};

export default PolicyViewDetails;